/**
 * Rides, bookings, standing requests, and the driver's own views.
 *
 * Paginated endpoints return `{ items, meta }`. The envelope's meta is read off
 * the RTK Query meta object rather than being folded into the array, so a
 * caller that does not care about paging can ignore it and one that does is not
 * left counting rows to guess whether more exist.
 */

import { api, LIST_ID } from "../api";
import type { EnvelopeMeta } from "../baseQuery";
import type {
    AfterRideView,
    BookingStatus,
    RideStatus,
    SearchMatch,
    BeforeRideView,
    BookRideRequest,
    BookingDetail,
    CancelRequest,
    CreateRideRequest,
    CounterFareRequest,
    CreateRideRequestRequest,
    DispatchOffer,
    RespondToOfferRequest,
    DecideBookingRequest,
    DriverOverview,
    DuringRideView,
    AddStopRequest,
    ComputedRoute,
    EarningsDashboard,
    EarningsSummary,
    EarningsSummaryArgs,
    Meta,
    Ride,
    RideDetail,
    RideProgress,
    RideRequest,
    RideStop,
    RouteRequest,
    RequestMatch,
    SearchResult,
    SearchRidesRequest,
    SetFareRequest,
    UpdateStopRequest,
    UUID,
} from "../types";

/** A page of results plus the envelope's pagination metadata. */
export interface Page<T> {
    items: T[];
    meta?: Meta;
}

export interface PageArgs {
    page?: number;
    /** Clamped server-side to 100. */
    limit?: number;
}

/** Ride history can be narrowed to one lifecycle status. */
export interface HistoryArgs extends PageArgs {
    status?: RideStatus;
}

/** Earnings accept an inclusive RFC3339 window alongside paging. */
export interface EarningsArgs extends PageArgs {
    from?: string;
    to?: string;
}

/** Folds the envelope's meta back in beside the rows. */
function paged<T>(items: T[] | null, meta: EnvelopeMeta | undefined): Page<T> {
    return { items: items ?? [], meta: meta?.envelope?.meta };
}

export const ridesApi = api.injectEndpoints({
    endpoints: (build) => ({
        // ── Discovery ───────────────────────────────────────────────────────

        /**
         * A corridor search. This is a POST because the journey is a body, not
         * a query string: two points, optional waypoints and a departure window
         * do not survive being flattened into a URL.
         *
         * The result carries the stored search's id, so a booking made from it
         * can be attributed back to the search that produced it.
         */
        searchRides: build.mutation<SearchResult, SearchRidesRequest>({
            query: (body) => ({ url: "rides/search", method: "POST", body }),
        }),

        // ── The caller's own rides ──────────────────────────────────────────

        rideHistory: build.query<Page<Ride>, HistoryArgs | void>({
            query: (args) => ({ url: "rides/mine", params: args ?? {} }),
            transformResponse: (rows: Ride[] | null, meta: EnvelopeMeta) =>
                paged(rows, meta),
            providesTags: (result) => [
                { type: "Ride" as const, id: LIST_ID },
                ...(result?.items ?? []).map((r) => ({
                    type: "Ride" as const,
                    id: r.id,
                })),
            ],
        }),

        upcomingRides: build.query<Ride[], void>({
            query: () => "rides/upcoming",
            transformResponse: (rows: Ride[] | null) => rows ?? [],
            providesTags: [{ type: "Ride", id: LIST_ID }],
        }),

        ride: build.query<RideDetail, UUID>({
            query: (rideId) => `rides/${rideId}`,
            providesTags: (_r, _e, rideId) => [{ type: "Ride", id: rideId }],
        }),

        /**
         * Where the driver has got to, for whoever is asking.
         *
         * Its own endpoint rather than a field on the ride, because the server
         * answers it differently for each side and the ride payload carries a
         * passenger no other passenger's stop to count. Tagged on the ride, so
         * advancing a stop refreshes it.
         */
        rideProgress: build.query<RideProgress, UUID>({
            query: (rideId) => `rides/${rideId}/progress`,
            providesTags: (_r, _e, rideId) => [
                { type: "Ride", id: rideId },
                { type: "RideStop", id: rideId },
            ],
        }),

        rideSummary: build.query<SearchMatch, UUID>({
            query: (rideId) => `rides/${rideId}/summary`,
            providesTags: (_r, _e, rideId) => [
                { type: "Ride", id: rideId },
                { type: "Booking", id: rideId },
            ],
        }),

        rideBookings: build.query<
            BookingDetail[],
            UUID | { rideId: UUID; status?: BookingStatus }
        >({
            query: (arg) => {
                const { rideId, status } =
                    typeof arg === "string" ? { rideId: arg, status: undefined } : arg;
                return {
                    url: `rides/${rideId}/bookings`,
                    params: status ? { status } : {},
                };
            },
            transformResponse: (rows: BookingDetail[] | null) => rows ?? [],
            providesTags: (_r, _e, arg) => [
                {
                    type: "Booking",
                    id: typeof arg === "string" ? arg : arg.rideId,
                },
            ],
        }),

        // ── The three stage views ───────────────────────────────────────────
        //
        // Which one to render is decided by ride.status, not by the client
        // guessing. Each returns exactly what its screen needs, including
        // whether the primary action is currently permitted, so a button's
        // enabled state and the endpoint's own precondition cannot disagree.

        beforeRide: build.query<BeforeRideView, UUID>({
            query: (rideId) => `rides/${rideId}/before-start`,
            providesTags: (_r, _e, rideId) => [{ type: "Ride", id: rideId }],
        }),

        duringRide: build.query<DuringRideView, UUID>({
            query: (rideId) => `rides/${rideId}/during`,
            providesTags: (_r, _e, rideId) => [{ type: "Ride", id: rideId }],
        }),

        afterRide: build.query<AfterRideView, UUID>({
            query: (rideId) => `rides/${rideId}/after`,
            providesTags: (_r, _e, rideId) => [
                { type: "Ride", id: rideId },
                { type: "Rating", id: rideId },
            ],
        }),

        // ── Driver: offering and running a ride ─────────────────────────────

        createRide: build.mutation<RideDetail, CreateRideRequest>({
            query: (body) => ({ url: "rides", method: "POST", body }),
            invalidatesTags: [
                { type: "Ride", id: LIST_ID },
                "DriverOverview",
            ],
        }),

        setFare: build.mutation<RideDetail, { rideId: UUID } & SetFareRequest>({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/fare`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "Ride", id: rideId },
                { type: "Booking", id: rideId },
            ],
        }),

        startRide: build.mutation<RideDetail, UUID>({
            query: (rideId) => ({ url: `rides/${rideId}/start`, method: "PUT" }),
            invalidatesTags: (_r, _e, rideId) => [
                { type: "Ride", id: rideId },
                { type: "Ride", id: LIST_ID },
                "DriverOverview",
            ],
        }),

        completeRide: build.mutation<RideDetail, UUID>({
            query: (rideId) => ({
                url: `rides/${rideId}/complete`,
                method: "PUT",
            }),
            invalidatesTags: (_r, _e, rideId) => [
                { type: "Ride", id: rideId },
                { type: "Ride", id: LIST_ID },
                "DriverOverview",
                "Earnings",
            ],
        }),

        cancelRide: build.mutation<RideDetail, { rideId: UUID } & CancelRequest>({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/cancel`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "Ride", id: rideId },
                { type: "Ride", id: LIST_ID },
                "DriverOverview",
            ],
        }),

        /** Standing passenger requests this driver's route could carry. */
        matchingRequests: build.query<RequestMatch[], UUID>({
            query: (rideId) => `rides/${rideId}/matches`,
            transformResponse: (rows: RequestMatch[] | null) => rows ?? [],
            providesTags: [{ type: "RideRequest", id: LIST_ID }],
        }),

        /** The driver accepting or rejecting a passenger's booking. */
        decideBooking: build.mutation<
            BookingDetail,
            { rideId: UUID; bookingId: UUID } & DecideBookingRequest
        >({
            query: ({ rideId, bookingId, ...body }) => ({
                url: `rides/${rideId}/bookings/${bookingId}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "Ride", id: rideId },
                { type: "Booking", id: rideId },
                "DriverOverview",
            ],
        }),

        driverOffers: build.query<Page<Ride>, PageArgs | void>({
            query: (args) => ({ url: "rides/driver/offers", params: args ?? {} }),
            transformResponse: (rows: Ride[] | null, meta: EnvelopeMeta) =>
                paged(rows, meta),
            providesTags: (result) => [
                { type: "Ride" as const, id: LIST_ID },
                ...(result?.items ?? []).map((r) => ({
                    type: "Ride" as const,
                    id: r.id,
                })),
            ],
        }),

        /**
         * Live dispatch offers waiting for this driver's answer.
         *
         * A bare array rather than a page: an offer lives five minutes at most,
         * so there is never a second page of them, and paging a list that
         * expires while you read it is a way to hold a stale one open.
         */
        driverRequests: build.query<DispatchOffer[], void>({
            query: () => "rides/driver/requests",
            transformResponse: (rows: DispatchOffer[] | null) => rows ?? [],
            providesTags: [{ type: "DispatchOffer", id: LIST_ID }],
        }),

        /**
         * Accept or decline one offer.
         *
         * Accepting answers the ride, re-read after the seat is reserved, so its
         * seat counts are the ones to render rather than any derived locally.
         * Declining answers nothing: the cascade has moved to another driver and
         * there is no longer anything belonging to this one.
         *
         * Both invalidate the overview and the offer list, because accepting
         * creates a ride this driver now owns and declining removes a row.
         */
        respondToOffer: build.mutation<
            RideDetail | null,
            { offerId: UUID } & RespondToOfferRequest
        >({
            query: ({ offerId, ...body }) => ({
                url: `rides/driver/requests/${offerId}`,
                method: "PUT",
                body,
            }),
            // The function form, so a REFUSAL invalidates nothing. A static array
            // is applied whether the mutation fulfilled or not, and a refused
            // answer would re-read the queue, drop the row and take the reason
            // it was refused away with it, leaving a driver who tapped Accept
            // with a shorter queue and no explanation.
            invalidatesTags: (_result, error) =>
                error
                    ? []
                    : [
                          { type: "DispatchOffer", id: LIST_ID },
                          { type: "Ride", id: LIST_ID },
                          "DriverOverview",
                      ],
        }),

        driverOverview: build.query<DriverOverview, void>({
            query: () => "rides/driver/overview",
            providesTags: ["DriverOverview"],
        }),

        /**
         * One summary object, not a list. The envelope's pagination metadata
         * describes `entries` within it, so paging the entries means passing
         * page/limit here and reading meta.totalPages off the envelope.
         */
        earnings: build.query<EarningsSummary, EarningsArgs | void>({
            query: (args) => ({
                url: "rides/driver/earnings",
                params: args ?? {},
            }),
            providesTags: ["Earnings"],
        }),

        // ── Passenger: booking and standing requests ────────────────────────

        bookRide: build.mutation<BookingDetail, { rideId: UUID } & BookRideRequest>({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/bookings`,
                method: "POST",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "Ride", id: rideId },
                { type: "Booking", id: rideId },
                { type: "Ride", id: LIST_ID },
            ],
        }),

        cancelBooking: build.mutation<null, { rideId: UUID } & CancelRequest>({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/bookings/mine`,
                method: "DELETE",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "Ride", id: rideId },
                { type: "Booking", id: rideId },
                { type: "Ride", id: LIST_ID },
            ],
        }),

        /**
         * Naming a different price instead of accepting or rejecting one.
         *
         * Neither driver-gated nor passenger-gated: which side is speaking is
         * decided server-side from the token against the booking, because a
         * payload that could name its own side could counter as the other one.
         *
         * Refusals to expect and render rather than treat as faults: countering
         * your own offer is a 409, and so is a fourth price on a booking that
         * has had three.
         */
        counterFare: build.mutation<
            BookingDetail,
            { bookingId: UUID } & CounterFareRequest
        >({
            query: ({ bookingId, ...body }) => ({
                url: `rides/bookings/${bookingId}/counter`,
                method: "PUT",
                body,
            }),
            // The function form, so a refused counter invalidates nothing. A
            // static array is applied whether the mutation fulfilled or not, and
            // re-reading the booking after a refusal replaces the screen that is
            // carrying the reason it was refused.
            invalidatesTags: (result, error) =>
                error
                    ? []
                    : [
                          { type: "Ride", id: result?.booking.rideId ?? LIST_ID },
                          { type: "Booking", id: result?.booking.rideId ?? LIST_ID },
                          { type: "Ride", id: LIST_ID },
                      ],
        }),

        /** The passenger accepting or rejecting a fare the driver proposed. */
        decideFare: build.mutation<
            BookingDetail,
            { bookingId: UUID } & DecideBookingRequest
        >({
            query: ({ bookingId, ...body }) => ({
                url: `rides/bookings/${bookingId}/fare-decision`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (result) => [
                { type: "Ride", id: result?.booking.rideId ?? LIST_ID },
                { type: "Booking", id: result?.booking.rideId ?? LIST_ID },
                { type: "Ride", id: LIST_ID },
            ],
        }),

        createRideRequest: build.mutation<RideRequest, CreateRideRequestRequest>({
            query: (body) => ({ url: "rides/requests", method: "POST", body }),
            invalidatesTags: [{ type: "RideRequest", id: LIST_ID }],
        }),

        rideRequests: build.query<Page<RideRequest>, PageArgs | void>({
            query: (args) => ({ url: "rides/requests", params: args ?? {} }),
            transformResponse: (rows: RideRequest[] | null, meta: EnvelopeMeta) =>
                paged(rows, meta),
            providesTags: [{ type: "RideRequest", id: LIST_ID }],
        }),

        cancelRideRequest: build.mutation<null, UUID>({
            query: (requestId) => ({
                url: `rides/requests/${requestId}`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "RideRequest", id: LIST_ID }],
        }),

        // ── The schedule: stops along a ride ────────────────────────────────

        /**
         * A ride's stops, pickups and dropoffs and any waypoints the driver
         * added.
         *
         * Tagged against the ride rather than a collection of its own: a stop
         * only ever changes in the context of one ride, and starting or
         * completing that ride already invalidates it, so a driver who marks a
         * passenger picked up and then starts the ride does not see a stale
         * schedule.
         */
        rideStops: build.query<RideStop[], UUID>({
            query: (rideId) => `rides/${rideId}/stops`,
            providesTags: (_r, _e, rideId) => [
                { type: "RideStop", id: rideId },
            ],
        }),

        addStop: build.mutation<RideStop, { rideId: UUID } & AddStopRequest>({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/stops`,
                method: "POST",
                body,
            }),
            // The ride itself is invalidated too: adding a stop recomputes the
            // route server-side, so the ride's own geometry and duration change
            // as a result of this call.
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "RideStop", id: rideId },
                { type: "Ride", id: rideId },
            ],
        }),

        updateStop: build.mutation<
            RideStop,
            { rideId: UUID; stopId: UUID } & UpdateStopRequest
        >({
            query: ({ rideId, stopId, ...body }) => ({
                url: `rides/${rideId}/stops/${stopId}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "RideStop", id: rideId },
                { type: "Ride", id: rideId },
            ],
        }),

        removeStop: build.mutation<null, { rideId: UUID; stopId: UUID }>({
            query: ({ rideId, stopId }) => ({
                url: `rides/${rideId}/stops/${stopId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_r, _e, { rideId }) => [
                { type: "RideStop", id: rideId },
                { type: "Ride", id: rideId },
            ],
        }),

        // ── Routing ─────────────────────────────────────────────────────────

        /**
         * Asks the server for a road route.
         *
         * A mutation rather than a query because the API takes a POST body, and
         * because the result is not a cached resource: it is computed for the
         * points passed in and the caller holds it. Check `source` before
         * drawing, it is "direct" when the server has no map provider and the
         * geometry is a straight line.
         */
        computeRoute: build.mutation<ComputedRoute, RouteRequest>({
            query: (body) => ({ url: "rides/route", method: "POST", body }),
        }),

        // ── Driver: the earnings chart ──────────────────────────────────────

        /**
         * Earnings bucketed for the dashboard chart.
         *
         * Distinct from `earnings`, which returns the individual rows. This
         * returns the periods and the totals over them, already cut in the
         * driver's own timezone, so the client never re-aggregates money it
         * would have to round a second time.
         */
        earningsSummary: build.query<
            EarningsDashboard,
            EarningsSummaryArgs | void
        >({
            query: (args) => ({
                url: "rides/driver/earnings/summary",
                params: {
                    ...(args?.period ? { period: args.period } : {}),
                    ...(args?.bucket ? { bucket: args.bucket } : {}),
                    ...(args?.from ? { from: args.from } : {}),
                    ...(args?.to ? { to: args.to } : {}),
                    // Sent by default so the server cuts weeks where the driver
                    // is rather than in UTC.
                    timezone:
                        args?.timezone ??
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            }),
            providesTags: ["Earnings"],
        }),
    }),
});

export const {
    useSearchRidesMutation,
    useRideHistoryQuery,
    useUpcomingRidesQuery,
    useRideQuery,
    useRideProgressQuery,
    useRideSummaryQuery,
    useRideBookingsQuery,
    useBeforeRideQuery,
    useDuringRideQuery,
    useAfterRideQuery,
    useCreateRideMutation,
    useSetFareMutation,
    useStartRideMutation,
    useCompleteRideMutation,
    useCancelRideMutation,
    useMatchingRequestsQuery,
    useDecideBookingMutation,
    useDriverOffersQuery,
    useDriverOverviewQuery,
    useEarningsQuery,
    useEarningsSummaryQuery,
    useBookRideMutation,
    useCancelBookingMutation,
    useDecideFareMutation,
    useCounterFareMutation,
    useDriverRequestsQuery,
    useRespondToOfferMutation,
    useCreateRideRequestMutation,
    useRideRequestsQuery,
    useCancelRideRequestMutation,
    useRideStopsQuery,
    useAddStopMutation,
    useUpdateStopMutation,
    useRemoveStopMutation,
    useComputeRouteMutation,
} = ridesApi;
